import type { OrderStatus, PaymentMethod } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import { intlLocale } from "@/lib/formatting/locale";

/** Display group used by the redesign — collapses raw OrderStatus into the
 * 6 visual buckets the design relies on. "incomplete" is the 7th, used
 * exclusively for draft orders (pre-submission, no payment/fulfilment meaning). */
export type DisplayStatusId =
  | "action_needed"
  | "confirmed"
  | "preparing"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "incomplete";

export type StatusToneEntry = {
  /** i18n key path; resolve with `t(...)` at the call site. */
  labelKey: string;
  dot: string;
  bg: string;
  fg: string;
};

/** Pill background: a 14% wash of the pill's own ink over the card surface.
 * Both operands are theme-aware, so light and dark can never drift apart —
 * the previous hardcoded `oklch(0.96 ...)` backgrounds stayed light while the
 * `var(--*)` inks flipped in dark mode, collapsing "Delivered" to 1.57:1. */
const tint = (ink: string): string =>
  `color-mix(in srgb, ${ink} 14%, var(--card))`;

export const DISPLAY_STATUS_TONE: Record<DisplayStatusId, StatusToneEntry> = {
  incomplete: {
    labelKey: keys.orders.statusIncomplete,
    dot: "var(--status-neutral)",
    bg: tint("var(--status-neutral)"),
    fg: "var(--status-neutral)",
  },
  action_needed: {
    labelKey: keys.orders.dispActionNeeded,
    dot: "var(--accent-500)",
    bg: tint("var(--accent-600)"),
    fg: "var(--accent-600)",
  },
  confirmed: {
    labelKey: keys.orderStatus.confirmed,
    dot: "var(--info-500)",
    bg: tint("var(--info-500)"),
    fg: "var(--status-blue)",
  },
  preparing: {
    labelKey: keys.orderStatus.preparing,
    dot: "var(--info-500)",
    bg: tint("var(--info-500)"),
    fg: "var(--status-blue)",
  },
  shipped: {
    labelKey: keys.orderStatus.shipped,
    dot: "var(--success-500)",
    bg: tint("var(--success-500)"),
    fg: "var(--success-text)",
  },
  delivered: {
    labelKey: keys.orderStatus.delivered,
    dot: "var(--success-500)",
    bg: tint("var(--success-500)"),
    // -text, not -500: the 500 green only reaches 4.14:1 on its own light-mode
    // wash. In dark mode --success-text resolves back to the 500 green.
    fg: "var(--success-text)",
  },
  cancelled: {
    labelKey: keys.orderStatus.cancelled,
    dot: "var(--error-500)",
    bg: tint("var(--error-500)"),
    fg: "var(--error-500)",
  },
};

/**
 * Map a raw `OrderStatus` to the redesign's display bucket.
 * "action_needed" merges all the seller-must-act buckets so a single pill
 * surfaces the global outstanding count.
 */
export function toDisplayStatus(status: OrderStatus): DisplayStatusId {
  switch (status) {
    case "draft":
      return "incomplete";
    case "pending_buyer_payment":
    case "pending_buyer_verification":
    case "pending_payment_verification":
    case "pending_seller_confirmation":
    case "paid_less_than_required":
      return "action_needed";
    case "confirmed":
      return "confirmed";
    case "preparing":
      return "preparing";
    case "shipped":
      return "shipped";
    case "delivered":
      return "delivered";
    case "cancelled":
    case "declined":
      return "cancelled";
  }
}

/** i18n key for the REAL per-status label (not the collapsed "action_needed"
 *  display bucket) — used by the status badge so rows show the true status. */
const STATUS_LABEL_KEY: Record<OrderStatus, string> = {
  draft: keys.orderStatus.draft,
  paid_less_than_required: keys.orderStatus.paidLessThanRequired,
  pending_buyer_verification: keys.orderStatus.pendingBuyerVerification,
  pending_buyer_payment: keys.orderStatus.pendingBuyerPayment,
  pending_payment_verification: keys.orderStatus.pendingPaymentVerification,
  pending_seller_confirmation: keys.orderStatus.pendingSellerConfirmation,
  confirmed: keys.orderStatus.confirmed,
  preparing: keys.orderStatus.preparing,
  shipped: keys.orderStatus.shipped,
  delivered: keys.orderStatus.delivered,
  cancelled: keys.orderStatus.cancelled,
  declined: keys.orderStatus.declined,
};

export function statusLabelKey(status: OrderStatus): string {
  return STATUS_LABEL_KEY[status];
}

/**
 * Build the 5-step timeline (Placed / Paid / Confirmed / Shipped / Delivered)
 * from a raw status. Steps go done in order — once `delivered` is reached,
 * everything before is done.
 */
export type TimelineStepId =
  | "placed"
  | "paid"
  | "confirmed"
  | "shipped"
  | "delivered";

/** i18n key path per timeline step; resolve with `t(...)` at the call site. */
export const TIMELINE_LABEL_KEYS: Record<TimelineStepId, string> = {
  placed: keys.orders.tlPlaced,
  paid: keys.orders.tlPaid,
  confirmed: keys.orderStatus.confirmed,
  shipped: keys.orderStatus.shipped,
  delivered: keys.orderStatus.delivered,
};

export function buildTimeline(
  status: OrderStatus,
  paymentMethod?: PaymentMethod,
): { id: TimelineStepId; done: boolean }[] {
  const ids: TimelineStepId[] = [
    "placed",
    "paid",
    "confirmed",
    "shipped",
    "delivered",
  ];
  const map: Partial<Record<OrderStatus, number>> = {
    draft: 0,
    pending_buyer_payment: 0,
    pending_buyer_verification: 0,
    pending_payment_verification: 1,
    pending_seller_confirmation: 0,
    paid_less_than_required: 1,
    confirmed: 2,
    preparing: 2,
    shipped: 3,
    delivered: 4,
    cancelled: 0,
    declined: 0,
  };
  const reached = map[status] ?? 0;
  const steps = ids.map((id, i) => ({ id, done: i <= reached }));
  // COD orders collect cash on delivery — there's no pre-delivery payment
  // milestone, so the "Paid" step is dropped from their timeline.
  return paymentMethod === "COD"
    ? steps.filter((step) => step.id !== "paid")
    : steps;
}

export function relativeAgo(iso: string, locale: string = "en"): string {
  if (typeof Intl === "undefined") return "";
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: "auto" });
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(ms / 60000);
  if (Math.abs(minutes) < 60) return rtf.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(-hours, "hour");
  const days = Math.round(hours / 24);
  return rtf.format(-days, "day");
}

/** Pseudo-stable color for a customer initial avatar background. */
export function avatarTint(seed: string): string {
  const palette = [
    "#f59e0b",
    "#34d399",
    "#60a5fa",
    "#f472b6",
    "#a78bfa",
    "#fb7185",
    "#22d3ee",
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

export function avatarInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]!.toUpperCase();
  return (parts[0][0]! + parts[parts.length - 1][0]!).toUpperCase();
}
